import React, { useEffect, useRef, useState } from 'react';
import { Card, CardBody, Col, Row } from 'reactstrap';
import OSelect from '../../../../Components/OElements/OSelect';
import GuideSelect from '../Components/GuideSelect';
import OInput from '../../../../Components/OElements/OInput';
import { DatePickerInput, DatesProvider } from '@mantine/dates';
import { useFormikContext } from 'formik';

// Helpers
import {
	getEstablishmenQuery,
	getPersonQuery,
	getSpeciesList,
} from '../../../../helpers/establishment/backend_helper';
import {
	getWarehouseList,
	getWarehouseListWithEstablishment,
	getRespelWarehouseList,
} from '../../../../helpers/warehouse/backend_helper';
import { useFormDictionary } from './FormDictionaryContext';

import * as Yup from 'yup';
import { toSmartTitleCase } from '../../../../utils/toSmartTitleCase';
import { warehouse_config } from '../../../../config';
import { CULTIVATION_CENTER_TYPE_IDS } from '../constants/guideConstants';
import { withoutWarehouseStockSendItemsRenumbered } from '../utils/warehouseStockSendAvailability';

const EPP_USAGE_TYPE_ID =
	warehouse_config.REACT_APP_WAREHOUSE_USAGE_TYPE_EPP_ID;
const RESPEL_USAGE_TYPE_ID =
	warehouse_config.REACT_APP_WAREHOUSE_USAGE_TYPE_RESPEL_ID || '';

export const OrigenSectionSpec = {
	getInitialValues: () => ({
		establishment_id: '',
		guide_date: null,
		responsible_id: '',
		transport_index: 6,

		// Datos de Origen
		company_name: '',
		company_rut: '',
		establishment_address: '',
		establishment_commune: '',
		company_business_activity: '',
		company_business_activity_code: '',
		delivery_type: 3,

		// EPP - Bodega de Origen
		warehouse_origin_id: '',

		// Campos Libres
		center_origin: '',
		siep_code_origin: '',
		responsible_emisor: '',
		responsible_rut: '',
		responsible_cargo: '',
	}),
	getValidationSchema: () =>
		Yup.object({
			establishment_id: Yup.string().required('Requerido'),
			guide_date: Yup.date().nullable().required('Requerido'),
			responsible_id: Yup.string().required('Requerido'),

			// Datos de Origen
			company_name: Yup.string().required('Requerido'),
			company_rut: Yup.string().required('Requerido'),
			establishment_address: Yup.string().required('Requerido'),
			establishment_commune: Yup.string().required('Requerido'),
			company_business_activity: Yup.string().required('Requerido'),
			company_business_activity_code: Yup.string().required('Requerido'),

			// EPP: bodega origen retiro/transferencia | Residuos: bodega RESPEL envío desde bodega peligrosos
			warehouse_origin_id: Yup.string().when(
				['guide_category', 'epp_operation_type', 'residuos_operation_type'],
				{
					is: (category, eppOp, residuosOp) =>
						(category === 'epp' &&
							(eppOp === 'retiro' || eppOp === 'transferencia')) ||
						(category === 'residuos' &&
							residuosOp === 'envio_bodega_peligrosos'),
					then: schema => schema.required('Requerido'),
					otherwise: schema => schema.notRequired(),
				}
			),
		}),
	toPayload: values => ({
		dtefacele: {
			Encabezado: {
				IdDoc: {
					FchEmis:
						values.guide_date?.toISOString() || new Date().toISOString(),
					TipoDespacho: values.delivery_type || '',
					IndTraslado: values.transport_index || '',
				},
				Emisor: {
					RUTEmisor: values.company_rut || '',
					RznSoc: values.company_name || '',
					GiroEmis: values.company_business_activity || '',
					DirOrigen: values.establishment_address || '',
					CmnaOrigen: values.establishment_commune || '',
					Acteco: values.company_business_activity_code || '',
				},
			},
			CamposLibres: {
				CentroOrigen: values.center_origin || '',
				CodigoSiepOrigen: values.siep_code_origin || '',
				Emisor: values.responsible_emisor || '',
				Rut: values.responsible_rut || '',
				Cargo: values.responsible_cargo || '',
			},
		},
	}),
};

export const OrigenSection = () => {
	const validation = useFormikContext();
	const prevWarehouseOriginRef = useRef('');

	// Estados para datos
	const [establishments, setEstablishments] = useState([]);
	const [persons, setPersons] = useState([]);
	const [selectedEstablishmentItem, setSelectedEstablishmentItem] =
		useState(null);
	const [isDetailsCollapsed, setIsDetailsCollapsed] = useState(true);

	// Detectar si es transferencia EPP
	const isTransferenciaEPP =
		validation.values.guide_category === 'epp' &&
		validation.values.epp_operation_type === 'transferencia';

	const isEnvioBodegaPeligrosos =
		validation.values.guide_category === 'residuos' &&
		validation.values.residuos_operation_type === 'envio_bodega_peligrosos';

	// Detectar si hay items valorizables (bloquea tipo de traslado)
	const hasValorizableItems =
		validation.values.items?.some(
			item => item._module_vars?.es_valorizable
		) || false;

	// Acceder al diccionario global
	const {
		setEstablishments: setDictionaryEstablishments,
		setWarehouses: setDictionaryWarehouses,
		setSpecies: setDictionarySpecies,
		setCultivationCenters: setDictionaryCultivationCenters,
	} = useFormDictionary();

	// Cargar establecimientos Y warehouses (una sola vez para todo el formulario)
	useEffect(() => {
		let cancelled = false;
		const loadFormData = async () => {
			try {
				// 1. Cargar establecimientos con populate completo
				const estResponse = await getEstablishmenQuery({
					fields: ['_id', 'name', 'address', 'code_siep', 'establishment_type_id'],
					populate: ['commune', 'company', 'establishment_status'],
					sort: ['name:asc'],
					filters: {
						disable: { $eq: false },
						establishment_status: { code: { $eq: 'ACT' } },
						company: { dte_emisor: { $eq: true } },
					},
					pagination: { page: 1, pageSize: 1000 },
				});

				if (cancelled) return;

				const establishmentOptions = estResponse.data?.map(item => ({
					value: item._id,
					label: item.name,
					item: item,
				}));

				setEstablishments(establishmentOptions);

				// Popular diccionario global
				setDictionaryEstablishments(estResponse.data || []);

				// 2. Cargar warehouses con establishment poblado
				const whResponse = await getWarehouseListWithEstablishment({
					populate: ['establishment'],
				});

				if (cancelled) return;

				// Popular diccionario global de warehouses
				setDictionaryWarehouses(whResponse || []);

				// 3. Cargar especies
				const speciesResponse = await getSpeciesList();
				if (cancelled) return;
				setDictionarySpecies(Array.isArray(speciesResponse) ? speciesResponse : (speciesResponse?.data || []));

				// 4. Cargar centros de cultivo (establecimientos tipo SWE, FWE, LWE)
				const centersResponse = await getEstablishmenQuery({
					fields: ['_id', 'name', 'code_siep'],
					sort: ['name:asc'],
					filters: {
						disable: { $eq: false },
						establishment_type_id: { $in: CULTIVATION_CENTER_TYPE_IDS },
					},
					pagination: { page: 1, pageSize: 1000 },
				});
				if (cancelled) return;
				setDictionaryCultivationCenters(centersResponse?.data || []);
			} catch (error) {
				if (!cancelled) {
					console.error('Error loading form data:', error);
				}
			}
		};

		loadFormData();
		return () => { cancelled = true; };
	}, []);

	// Cargar equipo de gestión cuando cambia el establecimiento
	useEffect(() => {
		const loadPersons = async () => {
			if (selectedEstablishmentItem) {
				try {
					const response = await getPersonQuery({
						populate: ['management_position'],
						filters: {
							company_id: {
								$eq: selectedEstablishmentItem.company._id,
							},
							disable: {
								$eq: false,
							},
						},
						sort: ['name:asc'],
						pagination: {
							page: 1,
							pageSize: 1000,
						},
					});
					const personsOptions = response.data.map(person => ({
						value: person._id,
						label:
							toSmartTitleCase(
								`${person.first_name} ${person.second_name} ${person.first_surname} ${person.second_surname} - ${person.rut}`
							) || 'Sin nombre',
						item: person,
					}));

					setPersons(personsOptions);
				} catch (error) {
					console.error('Error loading persons:', error);
				}
			} else {
				setPersons([]);
			}
		};

		loadPersons();
	}, [selectedEstablishmentItem]);

	useEffect(() => {
		const curr = validation.values.warehouse_origin_id || '';
		if (!isEnvioBodegaPeligrosos) {
			prevWarehouseOriginRef.current = curr;
			return;
		}
		const prev = prevWarehouseOriginRef.current;
		if (prev && curr && prev !== curr) {
			const items = validation.values.items || [];
			const cleaned = withoutWarehouseStockSendItemsRenumbered(items);
			if (cleaned.length !== items.length) {
				validation.setFieldValue('items', cleaned, false);
			}
		}
		prevWarehouseOriginRef.current = curr;
	}, [
		isEnvioBodegaPeligrosos,
		validation.values.warehouse_origin_id,
		validation.setFieldValue,
		validation.values.items,
	]);

	const handleEstablishmentSelected = establishment => {
		setSelectedEstablishmentItem(establishment);
		validation.setFieldValue(
			'establishment_id',
			establishment?._id || establishment?.value || ''
		);
		validation.setFieldValue('center_origin', establishment?.name || '');
		validation.setFieldValue(
			'siep_code_origin',
			establishment?.code_siep || ''
		);
		validation.setFieldValue(
			'establishment_type',
			establishment?.establishment_type_id || ''
		);

		// Reset responsable y bodega cuando cambia establecimiento
		validation.setFieldValue('responsible_id', '');
		validation.setFieldValue('warehouse_origin_id', '');
		prevWarehouseOriginRef.current = '';

		if (isEnvioBodegaPeligrosos) {
			const items = validation.values.items || [];
			const cleaned = withoutWarehouseStockSendItemsRenumbered(items);
			if (cleaned.length !== items.length) {
				validation.setFieldValue('items', cleaned, false);
			}
		}
	};

	const handlePersonSelected = person => {
		validation.setFieldValue(
			'responsible_id',
			person?._id || person?.value || ''
		);
		validation.setFieldValue(
			'responsible_emisor',
			toSmartTitleCase(
				`${person?.first_name} ${person?.second_name} ${person?.first_surname} ${person?.second_surname}`
			) || ''
		);
		validation.setFieldValue('responsible_rut', person?.rut || '');
		validation.setFieldValue(
			'responsible_cargo',
			person?.management_position?.name || ''
		);
	};

	// cargar datos titular
	useEffect(() => {
		if (selectedEstablishmentItem) {
			validation.setFieldValue(
				'company_name',
				selectedEstablishmentItem?.company?.name
			);
			validation.setFieldValue(
				'company_rut',
				selectedEstablishmentItem?.company?.rut
			);
			validation.setFieldValue(
				'establishment_address',
				selectedEstablishmentItem?.address
			);
			validation.setFieldValue(
				'establishment_commune',
				selectedEstablishmentItem?.commune?.name
			);
			validation.setFieldValue(
				'company_business_activity',
				selectedEstablishmentItem?.company?.business_activity
			);
			validation.setFieldValue(
				'company_business_activity_code',
				selectedEstablishmentItem?.company?.business_activity_code
			);
		}
	}, [selectedEstablishmentItem]);

	const deliveryTypeOptions = [
		{
			value: 1,
			label: '1: Despacho por cuenta del receptor del documento',
			item: {
				code: 1,
				name: '1: Despacho por cuenta del receptor del documento',
			},
		},
		{
			value: 2,
			label: '2: Despacho por cuenta del emisor a instalaciones del cliente',
			item: {
				code: 2,
				name: '2: Despacho por cuenta del emisor a instalaciones del cliente',
			},
		},
		{
			value: 3,
			label: '3: Despacho por cuenta del emisor a otras instalaciones',
			item: {
				code: 3,
				name: '3: Despacho por cuenta del emisor a otras instalaciones',
			},
		},
	];

	const transportIndexOptions = [
		{
			value: 1,
			label: '1: Operación constituye venta',
			item: { code: 1, name: '1: Operación constituye venta' },
		},
		{
			value: 2,
			label: '2: Ventas por efectuar',
			item: { code: 2, name: '2: Ventas por efectuar' },
		},
		{
			value: 3,
			label: '3: Consignaciones',
			item: { code: 3, name: '3: Consignaciones' },
		},
		{
			value: 4,
			label: '4: Entrega gratuita',
			item: { code: 4, name: '4: Entrega gratuita' },
		},
		{
			value: 5,
			label: '5: Traslados internos',
			item: { code: 5, name: '5: Traslados internos' },
		},
		{
			value: 6,
			label: '6: Otros traslados no venta',
			item: { code: 6, name: '6: Otros traslados no venta' },
		},
		{
			value: 7,
			label: '7: Guía de devolución',
			item: { code: 7, name: '7: Guía de devolución' },
		},
		{
			value: 8,
			label: '8: Traslado para exportación',
			item: { code: 8, name: '8: Traslado para exportación' },
		},
		{
			value: 9,
			label: '9: Venta para exportación',
			item: { code: 9, name: '9: Venta para exportación' },
		},
	];

	return (
		<Card className='border mb-0'>
			<CardBody className='p-4'>
				<div className='pb-2'>
					<h5>Información de Origen</h5>
					<p className='text-muted'>
						Complete los datos básicos de la guía de despacho.
					</p>
				</div>
				<Row className='g-3'>
					{/* Establecimiento */}
					<Col lg={3}>
						<OSelect
							key={`establishment-${establishments.length}`}
							label='Establecimiento'
							id='establishment_id'
							name='establishment_id'
							value={validation.values.establishment_id || ''}
							onSelect={handleEstablishmentSelected}
							itemsSelect={establishments}
							validation={validation}
							isRequired
						/>
					</Col>

					{/* Fecha */}
					<Col lg={2}>
						<DatesProvider
							settings={{
								locale: 'es-mx',
								firstDayOfWeek: 1,
								weekendDays: [0],
								timezone: 'UTC',
							}}
						>
							<DatePickerInput
								label='Fecha de Guía'
								id='guide_date'
								name='guide_date'
								placeholder='Seleccione una fecha'
								value={validation.values.guide_date}
								onChange={date => {
									validation.setFieldValue('guide_date', date);
								}}
								onBlur={validation.handleBlur}
								valueFormat='DD-MM-YYYY'
								maxDate={new Date()}
								error={
									validation.touched['guide_date'] &&
									validation.errors['guide_date']
										? validation.errors['guide_date']
										: ''
								}
								clearable={true}
							/>
						</DatesProvider>
					</Col>

					{/* Responsable */}
					<Col lg={4}>
						<OSelect
							label='Responsable'
							id='responsible_id'
							name='responsible_id'
							value={validation.values.responsible_id || ''}
							onSelect={handlePersonSelected}
							itemsSelect={persons}
							handleLabelOptions={item =>
								item?.name || 'Sin responsable'
							}
							validation={validation}
							// disabled={!validation.values.establishment_id}
							isRequired
						/>
					</Col>
					{/* Tipo de Transporte */}
					<Col lg={3}>
						<GuideSelect
							label='Tipo de Traslado'
							id='transport_index'
							name='transport_index'
							value={validation.values.transport_index || ''}
							itemsSelect={transportIndexOptions}
							validation={validation}
							isRequired
							disabled={hasValorizableItems}
							labelExtra={hasValorizableItems ? '(Auto: Residuo valorizado)' : ''}
						/>
					</Col>

					{/* Bodega de Origen (Solo Transferencia EPP) */}
					{isTransferenciaEPP && (
						<Col lg={3}>
							<OSelect
								key={`warehouse_origin_id_${
									validation.values.establishment_id || 'none'
								}`}
								label='Bodega EPP de Origen'
								id='warehouse_origin_id'
								name='warehouse_origin_id'
								isRequired={true}
								value={validation.values.warehouse_origin_id || ''}
								requestApi={() =>
									validation.values.establishment_id
										? getWarehouseList({
												establishment_id:
													validation.values.establishment_id,
												warehouse_usage_type_id: EPP_USAGE_TYPE_ID,
										  })
										: Promise.resolve([])
								}
								handleLabelOptions={item =>
									`${item?.name || ''} ${
										item?.code_sap ? `(${item.code_sap})` : ''
									}`
								}
								validation={validation}
								disabled={!validation.values.establishment_id}
								onSelect={warehouse => {
									// Guardar código SAP para uso en ItemsSelector
									validation.setFieldValue(
										'_warehouse_origin_code',
										warehouse?.code_sap || '',
										false
									);
								}}
								placeholder={
									validation.values.establishment_id
										? 'Seleccione bodega EPP...'
										: 'Primero seleccione establecimiento'
								}
							/>
						</Col>
					)}

					{isEnvioBodegaPeligrosos && (
						<Col lg={3}>
							<OSelect
								key={`respel_warehouse_origin_${
									validation.values.establishment_id || 'none'
								}`}
								label='Bodega RESPEL de Origen'
								id='warehouse_origin_id'
								name='warehouse_origin_id'
								isRequired
								value={validation.values.warehouse_origin_id || ''}
								requestApi={() =>
									validation.values.establishment_id
										? getRespelWarehouseList(
												validation.values.establishment_id,
										  )
										: Promise.resolve([])
								}
								handleLabelOptions={item =>
									item?.name || item?.code_sap || 'Sin nombre'
								}
								validation={validation}
								disabled={!validation.values.establishment_id}
								placeholder={
									!RESPEL_USAGE_TYPE_ID
										? 'Falta REACT_APP_WAREHOUSE_USAGE_TYPE_RESPEL_ID'
										: validation.values.establishment_id
											? 'Seleccione bodega RESPEL...'
											: 'Primero seleccione establecimiento'
								}
							/>
						</Col>
					)}

					{/* Divider with centered toggle */}
					<Col lg={12} className='p-0'>
						<div className='divider-with-toggle'>
							<hr className='hr' />
							<button
								type='button'
								className='divider-toggle-btn'
								aria-expanded={!isDetailsCollapsed}
								onClick={() => setIsDetailsCollapsed(prev => !prev)}
							>
								<i className='ri-arrow-up-s-line'></i>
							</button>
						</div>
					</Col>
					{/* Datos del Titular (autocompletados) */}
					<Col lg={12} className='p-0'>
						<div
							className={
								'collapsible-content' +
								(isDetailsCollapsed ? ' is-collapsed' : '')
							}
						>
							<Row className='g-3'>
								<Col lg={2}>
									<OInput
										id='company_name'
										name='company_name'
										type='text'
										label='Razón Social Titular'
										className='form-control'
										value={validation.values.company_name || ''}
										validation={validation}
										disabled
										readOnly
									/>
								</Col>
								<Col lg={2}>
									<OInput
										id='company_rut'
										name='company_rut'
										type='text'
										label='Rut Titular'
										className='form-control'
										value={validation.values.company_rut || ''}
										validation={validation}
										disabled
										readOnly
									/>
								</Col>
								<Col lg={6}>
									<OInput
										id='establishment_address'
										name='establishment_address'
										label='Dirección Establecimiento'
										type='text'
										className='form-control'
										value={
											validation.values.establishment_address || ''
										}
										validation={validation}
										disabled
										readOnly
									/>
								</Col>
								<Col lg={2}>
									<OInput
										id='establishment_commune'
										name='establishment_commune'
										type='text'
										label='Comuna Establecimiento'
										className='form-control'
										value={
											validation.values.establishment_commune || ''
										}
										validation={validation}
										disabled
										readOnly
									/>
								</Col>
								<Col lg={4}>
									<OInput
										id='company_business_activity'
										name='company_business_activity'
										label='Giro Titular'
										type='text'
										className='form-control'
										placeholder='Sin Giro en DB'
										value={
											validation.values.company_business_activity ||
											''
										}
										validation={validation}
										disabled
										readOnly
									/>
								</Col>
								<Col lg={4}>
									<OInput
										id='company_business_activity_code'
										name='company_business_activity_code'
										label='Cod. Act. Económica Titular'
										type='text'
										className='form-control'
										placeholder='Sin Código en DB'
										value={
											validation.values
												.company_business_activity_code || ''
										}
										validation={validation}
										disabled
										readOnly
									/>
								</Col>
								<Col lg={4}>
									<GuideSelect
										label='Tipo de Despacho'
										id='delivery_type'
										name='delivery_type'
										value={validation.values.delivery_type || ''}
										itemsSelect={deliveryTypeOptions}
										validation={validation}
										disabled
									/>
								</Col>
							</Row>
						</div>
					</Col>
				</Row>
			</CardBody>
		</Card>
	);
};
